import { useMemo, useState } from 'react';
import {
  QualityStatusTypes,
  Candidate,
  OpenToWorkTypes,
} from '$types/candidates.d';
import { useTranslation } from 'react-i18next';
import {
  useUpdateCandidateOpenToWorkMutation,
  useUpdateCandidateQualityStatusMutation,
} from '$store/modules/candidates';
import {
  GetCandidateAiScoreOutput,
  useGetCandidateAiScoreQuery,
} from '$store/modules/candidate-ai';
import Spinner from '$components/Spinner/Spinner';

import CandidateModifyStatus from '../CandidateCard/CandidateModifyStatus/CandidateModifyStatus';
import SingleAiScoreOverride from './SingleAiScoreOverride/SingleAiScoreOverride';

const ScoresTabContainer = ({ candidate }: { candidate: Candidate }) => {
  const [t] = useTranslation('global');
  const [tababbleData, setTabbableData] = useState<null | {
    group: string;
    editingIndex: number;
  }>(null);

  const [updateCandidateQuality] = useUpdateCandidateQualityStatusMutation();

  const { data: scoresData, isLoading: isLoadingCandidateAiScores } =
    useGetCandidateAiScoreQuery(candidate.Id);

  const saveQualityStatus = async (newValue: string) => {
    await updateCandidateQuality({
      QualityStatus: newValue as QualityStatusTypes,
      CandidateId: candidate?.Id as number,
    });
  };

  const getQualityStatusColor = (value: string) => {
    switch (value) {
      case QualityStatusTypes['5-Star']:
      case QualityStatusTypes.Good:
        return 'bg-internal-main';
      case QualityStatusTypes.Farm:
        return 'bg-blue-500';
      case QualityStatusTypes.Bad:
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const [updateCandidateOpenToWork] = useUpdateCandidateOpenToWorkMutation();

  const saveOpenToWork = async (newValue: string) => {
    await updateCandidateOpenToWork({
      OpenToWork: newValue as OpenToWorkTypes,
      CandidateId: candidate?.Id as number,
    });
  };

  const getOpenToWorkColor = (value: OpenToWorkTypes) => {
    switch (value) {
      case OpenToWorkTypes.Active:
      case OpenToWorkTypes.Interested:
        return 'bg-internal-main';
      case OpenToWorkTypes['Not Available']:
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const groupedScores = useMemo(() => {
    if (scoresData) {
      return scoresData.reduce(
        (prev, act) => {
          if (!prev[act.ScoreGroup]) prev[act.ScoreGroup] = [];
          prev[act.ScoreGroup].push(act);
          return prev;
        },
        {} as Record<any, any>
      );
    }
    return {};
  }, [scoresData]);

  return (
    <section className="bg-white p-8 rounded">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 ">
        <CandidateModifyStatus
          title={t('SearchCandidates.Table.QualityStatus')}
          id="qualityStatus"
          onSave={saveQualityStatus}
          value={candidate?.QualityStatus}
          options={(
            Object.keys(QualityStatusTypes) as Array<
              keyof typeof QualityStatusTypes
            >
          ).reduce(
            (prev, act) => {
              const tempData = {
                value: act,
                text: QualityStatusTypes[act] as string,
                style: getQualityStatusColor(QualityStatusTypes[act]),
              };
              prev.push(tempData);
              return prev;
            },
            [] as { text: string; value: string; style: string }[]
          )}
        />
        <CandidateModifyStatus
          title={t('SearchCandidates.Table.OpenToWork')}
          id="openToWork"
          onSave={saveOpenToWork}
          value={candidate?.OpenToWork}
          options={(
            Object.keys(OpenToWorkTypes) as Array<keyof typeof OpenToWorkTypes>
          ).reduce(
            (prev, act) => {
              const tempData = {
                value: act,
                text: OpenToWorkTypes[act] as string,
                style: getOpenToWorkColor(OpenToWorkTypes[act]),
              };
              prev.push(tempData);
              return prev;
            },
            [] as { text: string; value: string; style: string }[]
          )}
        />
      </div>
      <div className="flex flex-row flex-wrap my-4">
        <div className="flex flex-row gap-2 pr-4">
          <span className="text-xl font-semibold text-nowrap">AI Scores</span>
        </div>
      </div>
      <div className="flex flex-col gap-8 flex-wrap justify-stretch">
        {isLoadingCandidateAiScores && (
          <Spinner secondary height="100%" width="100%" />
        )}
        {!isLoadingCandidateAiScores &&
          Object.keys(groupedScores).map((singleGroup, groupIndex) => {
            return (
              <div key={`container_${singleGroup}`}>
                <div className="flex flex-row items-center relative my-6">
                  <span className="text-nowrap text-xs absolute ml-6 rounded bg-internal-main px-2 py-1 text-white">
                    {singleGroup}
                  </span>
                  <hr className="w-full border-internal-main" />
                </div>
                <div className="flex flex-row gap-8 justify-stretch flex-wrap">
                  {groupedScores[singleGroup].map(
                    (
                      singleScore: GetCandidateAiScoreOutput[number],
                      index: number
                    ) => (
                      <SingleAiScoreOverride
                        key={singleScore.Slug}
                        scoreData={singleScore}
                        candidateId={candidate.Id}
                        tabbableData={tababbleData}
                        index={index}
                        groupName={singleGroup}
                        onTabToNextInput={() => {
                          if (groupedScores[singleGroup][index + 1]) {
                            // Jumps into the next property of the same category
                            setTabbableData({
                              group: singleGroup,
                              editingIndex: index + 1,
                            });
                          } else if (
                            Object.keys(groupedScores)[groupIndex + 1]
                          ) {
                            // Jumps into the next category
                            setTabbableData({
                              group: Object.keys(groupedScores)[groupIndex + 1],
                              editingIndex: 0,
                            });
                          } else {
                            // Its the end, blocks all
                            setTabbableData(null);
                          }
                        }}
                      />
                    )
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
};

export default ScoresTabContainer;
